import type { ScreenType, StandardState } from '../../../types';

import GemMeterBaseRule from '../GemMeterBaseRule';
import {
  getElementGroup,
  getElementOfRule,
  getElementsOfRules,
  getPageMetric,
  getStandardByRuleId,
} from '../../Engine/useGemMeterStore';
import { DIFFERENT_HEADING_FONT_FAMILY } from '../../../constants/rulesConstants';
import GemMeterHelper from '../../Engine/v2/GemMeterHelper';

// specialKey
// ADD-elementMerId
// REMOVE-elementMerId
// EDIT-elementMerId
export default class DifferentHeadingFontFamily extends GemMeterBaseRule {
  static ruleId = DIFFERENT_HEADING_FONT_FAMILY.id;

  static validate(specialKey: string, screenType: ScreenType) {
    const ruleId = DifferentHeadingFontFamily.ruleId;
    const standards = getStandardByRuleId(ruleId);
    const action = specialKey.split('@').shift();
    const elementMerId = specialKey.split('@').pop() as string;

    if (action === 'ADD') {
      DifferentHeadingFontFamily.onAddElement(standards, screenType, elementMerId);
      DifferentHeadingFontFamily.runAddOnOtherScreen(standards, screenType, elementMerId);
    } else if (action === 'REMOVE') {
      DifferentHeadingFontFamily.onRemoveElement(standards, screenType, elementMerId);
      DifferentHeadingFontFamily.runRemoveOnOtherScreen(standards, screenType, elementMerId);
    } else if (action === 'EDIT') {
      DifferentHeadingFontFamily.onEditElement(standards, screenType, elementMerId);
    } else if (action === 'SHOW') {
      DifferentHeadingFontFamily.onAddElement(standards, screenType, elementMerId);
    } else if (action === 'HIDE') {
      DifferentHeadingFontFamily.onRemoveElement(standards, screenType, elementMerId);
    }

    return true;
  }

  static isValid(screenType: ScreenType) {
    const elementGroups = getElementGroup();
    const elementGroup = elementGroups[screenType][DIFFERENT_HEADING_FONT_FAMILY.id] || {};
    const elementOfRules = getElementsOfRules();
    let previousFontFamily = null;
    let isValid = true;

    for (const elementMerId of Object.keys(elementGroup)) {
      let domElement = GemMeterBaseRule.getElement(`${elementMerId} .text-edit :first-child`);
      if (!domElement) {
        domElement = GemMeterBaseRule.getElement(`${elementMerId} .text-edit`);
      }
      if (domElement && elementOfRules[elementMerId][screenType].visible) {
        const fontFamily = getComputedStyle(domElement).fontFamily;
        if (previousFontFamily !== null && previousFontFamily !== fontFamily) {
          isValid = false;
          break;
        }

        previousFontFamily = fontFamily;
      }
    }

    return isValid;
  }

  static onAddElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const metric = getPageMetric();
    const ruleId = DifferentHeadingFontFamily.ruleId;
    standards[ruleId].screen[screenType].elementLength += 1;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'fixed') {
      const isValid = DifferentHeadingFontFamily.isValid(screenType);
      if (isValid) {
        metric[screenType].fixed += 1;
        standards[ruleId].screen[screenType].status = 'fixed';
      } else {
        const elementGroups = getElementGroup();

        const elementGroup = elementGroups[screenType][DIFFERENT_HEADING_FONT_FAMILY.id] || {};

        metric[screenType].fixed -= Object.keys(elementGroup).length - 1;
        metric[screenType].errors += Object.keys(elementGroup).length;
        standards[ruleId].screen[screenType].status = 'error';

        for (const elementMerIdIn of Object.keys(elementGroup)) {
          const gemMeterElement = GemMeterHelper.createGemMeterElement('heading', elementMerIdIn);
          standards[ruleId].screen[screenType].errorElements[elementMerIdIn] = gemMeterElement;
        }
      }
    } else if (currentStatus === 'error') {
      metric[screenType].errors += 1;
      const gemMeterElement = GemMeterHelper.createGemMeterElement('heading', elementMerId);
      standards[ruleId].screen[screenType].errorElements[elementMerId] = gemMeterElement;
    } else if (currentStatus === 'skipped') {
      metric[screenType].skipped += 1;
      const gemMeterElement = GemMeterHelper.createGemMeterElement('heading', elementMerId);
      gemMeterElement.status = 'skip';
      gemMeterElement.visible = false;
      standards[ruleId].screen[screenType].errorElements[elementMerId] = gemMeterElement;
    } else {
      const isValid = DifferentHeadingFontFamily.isValid(screenType);
      if (isValid) {
        metric[screenType].fixed += 1;
        standards[ruleId].screen[screenType].status = 'fixed';
      } else {
        metric[screenType].errors += 1;
        standards[ruleId].screen[screenType].status = 'error';
      }
    }
  }

  static runAddOnOtherScreen(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const metric = getPageMetric();
    const ruleId = DifferentHeadingFontFamily.ruleId;
    (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenT) => {
      if (screenT !== screenType) {
        standards[ruleId].screen[screenT].elementLength += 1;
        switch (standards[ruleId].screen[screenT].status) {
          case 'error': {
            metric[screenT].errors += 1;
            const gemMeterElement = GemMeterHelper.createGemMeterElement('heading', elementMerId);
            standards[ruleId].screen[screenT].errorElements[elementMerId] = gemMeterElement;
            break;
          }
          case 'skipped': {
            metric[screenT].skipped += 1;
            const gemMeterElement = GemMeterHelper.createGemMeterElement('heading', elementMerId);
            gemMeterElement.status = 'skip';
            gemMeterElement.visible = false;
            standards[ruleId].screen[screenT].errorElements[elementMerId] = gemMeterElement;
            break;
          }
          default:
            metric[screenT].fixed += 1;
            break;
        }
      }
    });
  }

  static onRemoveElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const metric = getPageMetric();
    const ruleId = DifferentHeadingFontFamily.ruleId;
    standards[ruleId].screen[screenType].elementLength -= 1;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'skipped') {
      metric[screenType].skipped -= 1;
      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
      if (standards[ruleId].screen[screenType].elementLength === 0) {
        standards[ruleId].screen[screenType].status = null;
      }
    } else if (currentStatus === 'error') {
      metric[screenType].errors -= 1;
      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
      if (standards[ruleId].screen[screenType].elementLength > 0) {
        const isValid = DifferentHeadingFontFamily.isValid(screenType);
        if (isValid) {
          metric[screenType].fixed += standards[ruleId].screen[screenType].elementLength;
          standards[ruleId].screen[screenType].status = 'fixed';
          standards[ruleId].screen[screenType].errorElements = {};
        }
      } else {
        standards[ruleId].screen[screenType].status = null;
      }
    } else {
      metric[screenType].fixed -= 1;
      if (standards[ruleId].screen[screenType].elementLength === 0) {
        standards[ruleId].screen[screenType].status = null;
      }
    }
  }

  static runRemoveOnOtherScreen(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const metric = getPageMetric();
    const ruleId = DifferentHeadingFontFamily.ruleId;
    (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenT) => {
      if (screenT !== screenType) {
        standards[ruleId].screen[screenT].elementLength -= 1;
        switch (standards[ruleId].screen[screenT].status) {
          case 'error': {
            metric[screenT].errors -= 1;
            delete standards[ruleId].screen[screenT].errorElements[elementMerId];
            break;
          }
          case 'skipped': {
            metric[screenT].skipped -= 1;
            delete standards[ruleId].screen[screenT].errorElements[elementMerId];
            break;
          }
          default: {
            metric[screenT].fixed -= 1;
            break;
          }
        }

        if (standards[ruleId].screen[screenT].elementLength === 0) {
          standards[ruleId].screen[screenT].status = null;
        }
      }
    });
  }

  static onEditElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const ruleId = DifferentHeadingFontFamily.ruleId;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'skipped') {
      return;
    }

    const metric = getPageMetric();
    const isValid = DifferentHeadingFontFamily.isValid(screenType);
    if (!isValid && currentStatus === 'fixed') {
      standards[ruleId].screen[screenType].status = 'error';
      metric[screenType].fixed -= standards[ruleId].screen[screenType].elementLength;
      metric[screenType].errors += standards[ruleId].screen[screenType].elementLength;
      const elementGroups = getElementGroup();
      const elementGroup = elementGroups[screenType][DIFFERENT_HEADING_FONT_FAMILY.id] || {};
      Object.values(elementGroup).forEach((element) => {
        const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);
        standards[ruleId].screen[screenType].errorElements[element.merId] = gemElement;
      });
    }

    if (isValid && currentStatus === 'error') {
      standards[ruleId].screen[screenType].status = 'fixed';
      metric[screenType].fixed += standards[ruleId].screen[screenType].elementLength;
      metric[screenType].errors -= standards[ruleId].screen[screenType].elementLength;
      const elementGroups = getElementGroup();
      const elementGroup = elementGroups[screenType][DIFFERENT_HEADING_FONT_FAMILY.id] || {};
      Object.values(elementGroup).forEach((element) => {
        delete standards[ruleId].screen[screenType].errorElements[element.merId];
      });
    }
  }
}
